import { SplitDirection } from "@cesium/engine";
// import ImageryProvider from "@cesium/engine/Source/Scene/ImageryProvider";
import DitapViewer from "../basic/DitapViewer";

/**
 * 분할을 위한 분석 기능 클래스.</br>
 * Analysis feature class for object deletion.
 * @alias AnalysisSplit
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Split' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   baseLayerPicker: true,
 * });
 *
 * // InteractionSplit 생성
 * const interactionSplit = new Ditap.InteractionSplit(viewer);
 *
 * // interactionSplit handler 실행 버튼
 * Sandcastle.addToolbarButton("Split On", async () => {
 *   // interactionSplit handler 실행
 *   interactionSplit.on(viewer);
 *
 *   // AnalysisSplit 인스턴스 생성
 *   const analysisSplit = interactionSplit.analysisSplit;
 *
 *   // 뷰어에 추가할 데이터 생성
 *   // 1. 정사영상
 *   // 정사영상 인스턴스 생성
 *   const imagery = new Ditap.NationwideOrthoImageryProvider();
 *
 *   // 2. 모델
 *   // 모델 좌표
 *   const modelPosition = new Ditap.Cartesian3.fromDegrees(
 *     126.9782349646649,
 *     37.56671536989183,
 *     100
 *   );
 *
 *   // 모델 행렬 생성
 *   const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *     modelPosition
 *   );
 *
 *   // 모델 인스턴스 생성
 *   const model = await Ditap.Model.fromGltfAsync({
 *     id: "model_01",
 *     url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *     modelMatrix: modelMatrix,
 *     minimumPixelSize: 128,
 *   });
 *
 *   // 분할된 뷰어에 생성된 인스턴스 추가
 *   // 우측 분할 뷰어에 정사영상 추가
 *   const rightImagery = analysisSplit.addImagery(
 *     Ditap.SplitDirection.RIGHT,
 *     imagery
 *   );
 *
 *   // 좌측 분할 뷰어에 모델 추가
 *   const leftModel = analysisSplit.addModel(
 *     Ditap.SplitDirection.LEFT,
 *     model
 *   );
 *
 *   // 모델 위치로 카메라 이동
 *   viewer.camera.flyTo({
 *     destination: Ditap.Cartesian3.fromDegrees(
 *       126.9782349646649,
 *       37.5645,
 *       150
 *     ),
 *     duration: 2,
 *     orientation: {
 *       heading: Ditap.Math.toRadians(0),
 *       pitch: Ditap.Math.toRadians(-10),
 *       roll: 0,
 *     },
 *   });
 * });
 *
 * // interactionSplit handler 종료 버튼
 * Sandcastle.addToolbarButton("Split Off", () => {
 *   // interactionSplit handler 종료
 *   // interactionSplit.off() - 모든 분할 뷰어에 생성된 객체가 제거됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.LEFT) - 좌측 분할 뷰어에 생성된 객체가 유지됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.RIGHT) - 우측 분할 뷰어에 생성된 객체가 유지됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.BOTH) - 좌측, 우측 모든 분할 뷰어에 생성된 객체가 유지됩니다.
 *   interactionSplit.off();
 * });
 *
 */
class AnalysisSplit {
  constructor(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    } else {
      console.error("AnalysisSplit.js: Type of parameter doesn't match");
    }
    this._leftTilesets = [];
    this._rightTilesets = [];
    this._leftImagerys = [];
    this._rightImagerys = [];
    this._leftModels = [];
    this._rightModels = [];
    this._slider = undefined;
    this._splitPosition = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    this._viewer = viewer;
  }
  get viewer() {
    return this._viewer;
  }

  /**
   * 좌측 DitapViewer에 생성된 타일셋 배열.<br>
   * Array of tilesets created in the left DitapViewer
   * @readonly
   */
  set leftTilesets(leftTilesets) {
    this._leftTilesets = leftTilesets;
  }
  get leftTilesets() {
    return this._leftTilesets;
  }

  /**
   * 우측 DitapViewer에 생성된 타일셋 배열.<br>
   * Array of tilesets created in the right DitapViewer.
   * @readonly
   */
  set rightTilesets(rightTilesets) {
    this._rightTilesets = rightTilesets;
  }
  get rightTilesets() {
    return this._rightTilesets;
  }

  /**
   * 좌측 DitapViewer에 생성된 이미지 배열.<br>
   * Array of Imagery created in the left DitapViewer.
   * @readonly
   */
  set leftImagerys(leftImagerys) {
    this._leftImagerys = leftImagerys;
  }
  get leftImagerys() {
    return this._leftImagerys;
  }

  /**
   * 우측 DitapViewer에 생성된 이미지 배열.<br>
   * Array of Imagery created in the right DitapViewer.
   * @readonly
   */
  set rightImagerys(rightImagerys) {
    this._rightImagerys = rightImagerys;
  }
  get rightImagerys() {
    return this._rightImagerys;
  }

  /**
   * 좌측 DitapViewer에 생성된 모델 배열.<br>
   * Array of Model created in the left DitapViewer.
   * @readonly
   */
  set leftModels(leftModels) {
    this._leftModels = leftModels;
  }
  get leftModels() {
    return this._leftModels;
  }

  /**
   * 우측 DitapViewer에 생성된 모델 배열.<br>
   * Array of Model created in the right DitapViewer.
   * @readonly
   */
  set rightModels(rightModels) {
    this._rightModels = rightModels;
  }
  get rightModels() {
    return this._rightModels;
  }

  /*
   * DitapViewer에 생성된 분할 슬라이더.<br>
   * Split slider created in viewer.
   * @type {Element}
   */
  set slider(slider) {
    this._slider = slider;
  }
  get slider() {
    return this._slider;
  }

  /*
   * 분할 위치.<br>
   * Split position
   * @type {Element}
   */
  set splitPosition(splitPosition) {
    if (this.viewer) {
      this.viewer.scene.splitPosition = splitPosition;
    } else {
      console.error("AnalysisSplit.js: no Viewer");
    }
    this._splitPosition = splitPosition;
  }
  get splitPosition() {
    return this._splitPosition;
  }

  /**
   * 분할 슬라이더 실행.<br>
   * Running the AnalysisSplit
   */
  on() {
    // 슬라이더 - 슬라이더 div 생성
    this.slider = this.createSlider();

    // 슬라이더 - 슬라이더 위치와 분할 위치 동기화
    this.splitPosition =
      this.slider.offsetLeft / this.slider.parentElement.offsetWidth;
  }

  /**
   * 분할 슬라이더 제거.<br>
   * Exit the AnalysisSplit
   * @param {SplitDirection} splitDirection
   * 파라미터가 없을 경우, 분할 뷰어에 생성된 모든 객체가 제거됩니다.</br>
   * SplitDirection 파라미터를 전달하면 해당 방향의 분할 뷰어 위치에 생성된 객체가 유지됩니다.</br>
   * When no parameter is provided, all objects created within the split viewer will be removed.<br>
   * Passing the SplitDirection parameter will maintain the objects created at the position of the split viewer in the specified direction.
   */
  destroy(splitDirection) {
    // 슬라이더 - 슬라이더 삭제
    this.removeSlider();
    this.splitPosition = undefined;
    this.slider = undefined;

    // this.removeAllSplitObjet();
    switch (splitDirection) {
      case SplitDirection.LEFT:
        this.removeAllImageryByType(SplitDirection.RIGHT);
        this.removeAllModelByType(SplitDirection.RIGHT);
        this.removeAllTilesetByType(SplitDirection.RIGHT);
        break;
      case SplitDirection.RIGHT:
        this.removeAllImageryByType(SplitDirection.LEFT);
        this.removeAllModelByType(SplitDirection.LEFT);
        this.removeAllTilesetByType(SplitDirection.LEFT);
        break;
      case SplitDirection.BOTH:
        break;
      default:
        this.removeAllSplitObjet();
        break;
    }
  }

  /**
   * DitapViewer에 타일셋 추가.<br>
   * Add tileset to viewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   * @param {Cesium3DTileset} tileset 추가할 타일셋.<br>The tileset to add.
   * @returns {object} 생성된 프리미티브.<br>Primitives added to the DitapViewer.
   *
   */
  addTileset(type, tileset) {
    if (this.viewer) {
      const primitive = this.viewer.scene.primitives.add(tileset);
      switch (type) {
        case SplitDirection.LEFT:
          primitive.splitDirection = SplitDirection.LEFT;
          this.leftTilesets = [...this.leftTilesets, primitive];
          break;

        case SplitDirection.RIGHT:
          primitive.splitDirection = SplitDirection.RIGHT;
          this.rightTilesets = [...this.rightTilesets, primitive];
          break;

        default:
          throw new Error("AnalysisSplit.js: no split viewer Type");
      }

      return primitive;
    }
  }

  /**
   * DitapViewer에 모델 추가.<br>
   * Add Model to viewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   * @param {Model} model 추가할 모델.<br>The Model to add
   * @returns {object} 생성된 프리미티브.<br>Primitives added to the DitapViewer.
   */
  addModel(type, model) {
    // * 모델 - viewer에 model을 추가하는 함수
    if (this.viewer) {
      const primitive = this.viewer.scene.primitives.add(model);

      switch (type) {
        case SplitDirection.LEFT:
          primitive.splitDirection = SplitDirection.LEFT;
          this.leftModels = [...this.leftModels, primitive];
          break;

        case SplitDirection.RIGHT:
          primitive.splitDirection = SplitDirection.RIGHT;
          this.rightModels = [...this.rightModels, primitive];
          break;

        default:
          throw new Error("Split.js: no split viewer Type");
      }

      return primitive;
    }
  }

  /**
   * DitapViewer에 이미지 추가.<br>
   * Add Imagery to viewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   * @param {ImageryProvider} ortho 추가할 이미지.<br>The Imagery to add.
   * @returns {object} 생성된 프리미티브.<br>Imagery added to the DitapViewer.
   */
  addImagery(type, ortho) {
    // viewer에 정사영상을 추가하는 함수
    if (this.viewer) {
      const imagery = this.viewer.imageryLayers.addImageryProvider(ortho);

      switch (type) {
        case SplitDirection.LEFT:
          imagery.splitDirection = SplitDirection.LEFT;
          this.leftImagerys = [...this.leftImagerys, imagery];
          break;

        case SplitDirection.RIGHT:
          imagery.splitDirection = SplitDirection.RIGHT;
          this.rightImagerys = [...this.rightImagerys, imagery];
          break;

        default:
          throw new Error("Split.js: no split viewer Type");
      }

      return imagery;
    }
  }

  /*
   * 분할 슬라이더 요소 생성.<br>
   * Create split slide Element.
   * @returns {Element} 슬라이더 요소.<br> slide Element.
   */
  createSlider() {
    // * 슬라이드 - ditapContainer에 슬라이드 div를 생성하는 함수
    const slider = document.createElement("div");
    slider.classList.add("js-split-slider", "ditap-split-line");
    slider.style.width = "10px";
    this.viewer.container.querySelector(".cesium-viewer").append(slider);

    return slider;
  }

  /*
   * 분할 슬라이더 요소 삭제.<br>
   * Remove split slide Element.
   */
  removeSlider() {
    const slider = this.slider;
    slider.remove();
  }

  /**
   * DitapViewer에 생성된 타일셋 삭제.<br>
   * Remove all tilesets added to the DitapViewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   */
  removeAllTilesetByType(type) {
    // *  타일셋 - viewer에 추가된 tileset를 삭제하는 함수
    switch (type) {
      case SplitDirection.LEFT:
        this.removePrimitives(this.leftTilesets);
        this.leftTilesets = [];
        break;

      case SplitDirection.RIGHT:
        this.removePrimitives(this.rightTilesets);
        this.rightTilesets = [];
        break;

      default:
        throw new Error("Split.js: removeTilesetByType 오류");
    }
  }

  /**
   * DitapViewer에 생성된 모델 삭제.<br>
   * Remove all models added to the DitapViewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   */
  removeAllModelByType(type) {
    switch (type) {
      case SplitDirection.LEFT:
        this.removePrimitives(this.leftModels);
        this.leftModels = [];
        break;

      case SplitDirection.RIGHT:
        this.removePrimitives(this.rightModels);
        this.rightModels = [];
        break;

      default:
        throw new Error("Split.js: removeModeltByType 오류");
    }
  }

  /**
   * DitapViewer에 생성된 이미지 삭제.<br>
   * Remove all Imagery added to the DitapViewer.
   * @param {SplitDirection} type 추가할 뷰어 방향.<br>Split direction of DitapViewer.
   */
  removeAllImageryByType(type) {
    switch (type) {
      case SplitDirection.LEFT:
        this.removeImageries(this.leftImagerys);
        this.leftImagerys = [];

        break;

      case SplitDirection.RIGHT:
        this.removeImageries(this.rightImagerys);
        this.rightImagerys = [];
        break;

      default:
        throw new Error("Split.js: removeImageryByType 오류");
    }
  }

  /**
   * DitapViewer에 생성된 프리미티브 삭제.<br>
   * Remove primitieves added to the DitapViewer.
   * @param {Primitive[]} primitives 삭제할 프리미티브 배열.<br>The primitives to remove.
   */
  removePrimitives(primitives) {
    const viewer = this.viewer;

    for (let i = 0; i < primitives.length; i += 1) {
      if (viewer.scene.primitives.contains(primitives[i])) {
        viewer.scene.primitives.remove(primitives[i]);
      }
    }
  }

  /**
   * DitapViewer에 생성된 이미지 삭제.<br>
   * Remove Imageries added to the DitapViewer.
   * @param {ImageryProvider[]} imageries 삭제할 이미지 배열.<br>The Imageries to remove.
   */
  removeImageries(imageries) {
    // * 정사영상 - viewer에서 파라미터로 받은 imageries를 삭제하는 함수
    const viewer = this.viewer;

    for (let i = 0; i < imageries.length; i += 1) {
      if (viewer.imageryLayers.contains(imageries[i])) {
        viewer.imageryLayers.remove(imageries[i]);
      }
    }
  }

  /**
   * DitapViewer에 생성된 모든 분할 객체 삭제.<br>
   * Remove All split object added to the DitapViewer.
   */
  removeAllSplitObjet() {
    // * 엔티티 - 분할객체에 생성된 모든 객체를 삭제한다.
    // 타일셋 - 양측 분할 뷰어 추가된 모든 타일셋 삭제
    this.removeAllTilesetByType(SplitDirection.LEFT);
    this.removeAllTilesetByType(SplitDirection.RIGHT);

    // 정사영상 - 양측 분할 뷰어 추가된 모든 정사영상 삭제
    this.removeAllImageryByType(SplitDirection.LEFT);
    this.removeAllImageryByType(SplitDirection.RIGHT);

    // 모델 - 양측 분할 뷰어 추가된 모든 모델 삭제
    this.removeAllModelByType(SplitDirection.LEFT);
    this.removeAllModelByType(SplitDirection.RIGHT);
  }
}

export default AnalysisSplit;
